import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

interface UserAgentRule {
    userAgent: string;
    disallow: string;
    allow: string;
    crawlDelay: string;
}

interface RobotsState {
    rules: UserAgentRule[];
    sitemaps: string;
    copied: boolean;
}

const PRESET_BOTS = ['*', 'Googlebot', 'Googlebot-Image', 'Bingbot', 'DuckDuckBot', 'YandexBot', 'Baiduspider', 'GPTBot', 'CCBot', 'Google-Extended', 'PerplexityBot', 'ClaudeBot', 'anthropic-ai'];

export default class RobotsTxtGeneratorComponent extends Component<{ feature?: string }, RobotsState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: RobotsState = {
        rules: [{ userAgent: '*', disallow: '', allow: '', crawlDelay: '' }],
        sitemaps: '',
        copied: false,
    };

    private generate(): string {
        const { rules, sitemaps } = this.state;
        const blocks: string[] = [];
        for (const r of rules) {
            const ua = r.userAgent.trim();
            if (!ua) continue;
            const lines: string[] = [`User-agent: ${ua}`];
            r.disallow.split('\n').map(s => s.trim()).filter(Boolean).forEach(p => lines.push(`Disallow: ${p}`));
            r.allow.split('\n').map(s => s.trim()).filter(Boolean).forEach(p => lines.push(`Allow: ${p}`));
            if (r.crawlDelay.trim()) lines.push(`Crawl-delay: ${r.crawlDelay.trim()}`);
            blocks.push(lines.join('\n'));
        }
        const sitemapLines = sitemaps.split('\n').map(s => s.trim()).filter(Boolean).map(s => `Sitemap: ${s}`);
        if (sitemapLines.length) blocks.push(sitemapLines.join('\n'));
        return blocks.join('\n\n');
    }

    private updateRule(idx: number, field: keyof UserAgentRule, value: string) {
        const rules = [...this.state.rules];
        rules[idx] = { ...rules[idx], [field]: value };
        this.setState({ rules });
    }

    private addRule = () => {
        this.setState({ rules: [...this.state.rules, { userAgent: '', disallow: '', allow: '', crawlDelay: '' }] });
    };

    private removeRule = (idx: number) => {
        if (this.state.rules.length === 1) return;
        this.setState({ rules: this.state.rules.filter((_, i) => i !== idx) });
    };

    copyOutput = () => {
        navigator.clipboard.writeText(this.generate()).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    downloadOutput = () => {
        const blob = new Blob([this.generate()], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'robots.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { rules, sitemaps, copied } = this.state;
        const output = this.generate();
        const lineCount = output.split('\n').length;

        const form = (
            <>
                {rules.map((rule, i) => (
                    <div key={i} className={genStyles.section}>
                        <div className={genStyles.sectionHeader}>
                            <span className={genStyles.sectionTitle}>User-agent rule {rules.length > 1 ? `#${i + 1}` : ''}</span>
                            {rules.length > 1 && (
                                <button className={genStyles.removeBtn} onClick={() => this.removeRule(i)} aria-label="Remove rule">✕</button>
                            )}
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor={`ua-${i}`}>User-agent</label>
                            <select id={`ua-${i}`} value={rule.userAgent}
                                onChange={e => this.updateRule(i, 'userAgent', e.target.value)}>
                                {PRESET_BOTS.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <div className={genStyles.fieldRow}>
                            <div className={genStyles.field}>
                                <label htmlFor={`disallow-${i}`}>Disallow paths</label>
                                <textarea id={`disallow-${i}`} rows={3} value={rule.disallow}
                                    placeholder={"/admin\n/api/private"}
                                    onChange={e => this.updateRule(i, 'disallow', e.target.value)} />
                                <span className={genStyles.field + ' hint'} style={{ fontSize: '0.72rem', color: '#94a3b8' }}>One per line</span>
                            </div>
                            <div className={genStyles.field}>
                                <label htmlFor={`allow-${i}`}>Allow paths</label>
                                <textarea id={`allow-${i}`} rows={3} value={rule.allow}
                                    placeholder={"/public"}
                                    onChange={e => this.updateRule(i, 'allow', e.target.value)} />
                                <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>One per line</span>
                            </div>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor={`delay-${i}`}>Crawl-delay (seconds, optional)</label>
                            <input id={`delay-${i}`} type="number" inputMode="decimal" min={0} value={rule.crawlDelay}
                                placeholder="e.g. 10"
                                onChange={e => this.updateRule(i, 'crawlDelay', e.target.value)} />
                        </div>
                    </div>
                ))}

                <button className={genStyles.addBtn} onClick={this.addRule}>+ Add User-agent rule</button>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}>
                        <span className={genStyles.sectionTitle}>Sitemaps</span>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="sitemaps">Sitemap URLs (one per line)</label>
                        <textarea id="sitemaps" rows={3} value={sitemaps}
                            placeholder="https://example.com/sitemap.xml"
                            onChange={e => this.setState({ sitemaps: e.target.value })} />
                    </div>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated robots.txt"
                previewMeta={`${lineCount} line${lineCount === 1 ? '' : 's'}`}
                preview={<CodeBlock code={output} language="bash" wrapLines maxHeight={500} minHeight={320} ariaLabel="Generated robots.txt" />}
                previewActions={<>
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput}>{copied ? '✓ Copied' : '📋 Copy'}</button>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`}
                        onClick={this.downloadOutput}>⬇ Download .txt</button>
                </>}
            />
        );
    }
}
